import path from 'path';

import { IdReference, OffsetPagedResults } from '~interfaces';
import { GeneralPagedParameters } from '~interfaces/requests';
import { AddressResponse, BaseReferenceResponse, PagedResponse } from '~interfaces/responses';
import { TopologyService } from '~services';

import ApprovalStateEnum from '../enums/approvalStateEnum';
import BookingStatusEnum from '../enums/bookingStatusEnum';
import UsageStateEnum from '../enums/usageStateEnum';
import Booking from '../interfaces/booking';
import BookingWriteable from '../interfaces/bookingWriteable';

class BookingsService extends TopologyService {
	public readonly path = 'bookings';

	public readonly hubSubPath = 'hub';

	constructor() {
		super('v1');
	}

	/**
	 * Create a new booking
	 * TODO: planboard vs booking endpoint?
	 * @param data
	 * @returns
	 */
	async createBooking(data: BookingWriteable): Promise<IdReference> {
		const content = BookingsService.toRequest(data);

		const response = await this._client.post('planboard', content);

		return response.data;
	}

	/**
	 * Get a paginated list of bookings
	 * @returns
	 */
	async getBookings({
		page = 1,
		pageSize = 10,
		sortBy = 'id',
		descending = true,
		...args
	}: GeneralPagedParameters<'id'>): Promise<OffsetPagedResults<Booking>> {
		const { data } = await this._client.get<PagedResponse<BookingResponse>>(this.path, {
			params: {
				pageNumber: page,
				pageSize: pageSize,
				providerId: args.organisationId,
				sortBy: sortBy,
				orderDescending: descending,
			},
		});

		return this.mapPagedResponse(data, BookingsService.fromResponse);
	}

	/**
	 * Get details of a single booking
	 * @param id
	 * @returns
	 */
	async getBookingById(id: string): Promise<Booking> {
		const { data } = await this._client.get<BookingResponse>(path.join(this.path, id));

		return BookingsService.fromResponse(data);
	}

	async sendBookingInvoice(
		bookingId: string,
		email: string,
		action: 'reuseExisting' | 'replace' | 'generateNew' = 'reuseExisting'
	): Promise<null> {
		await this._client.post(path.join(this.path, bookingId, 'sendInvoice'), {
			params: {
				emailAddress: email,
				action: action,
			},
		});

		return null;
	}

	async changeBookingHub(bookingId: string, hubId: string): Promise<BookingResponse> {
		const content = { value: hubId };
		const { data } = await this._client.put(
			path.join(this.path, bookingId, this.hubSubPath),
			content
		);
		return data;
	}

	/**
	 * Delete a booking
	 * @param id
	 * @returns
	 */
	async deleteBooking(id: string): Promise<null> {
		const { data } = await this._client.get<null>(path.join(this.path, id));

		return data;
	}

	static fromResponse(data: BookingResponse): Booking {
		const {
			id,
			name,
			periodStart,
			periodEnd,
			itemInstance,
			hubReference,
			userReference,
			startAddress,
			stopAddress,
			...rest
		} = data;

		return {
			...rest,
			...BookingsService.fromBaseReferenceResponse({
				id: id,
				name: name,
			}),
			startAddress: startAddress,
			stopAddress: stopAddress,
			period: {
				start: new Date(periodStart),
				end: new Date(periodEnd),
			},
			user: BookingsService.fromBaseReferenceResponse(userReference),
			item: {
				...itemInstance,
				device:
					itemInstance.deviceId != null ?
						{
							id: itemInstance.deviceId.toString(),
						}
					:	undefined,
				...BookingsService.fromBaseReferenceResponse(itemInstance),
			},
			hub: {
				address:
					hubReference.address != null ?
						{
							countryCode: hubReference.address.countryCode,
							country: hubReference.address.country,
							city: hubReference.address.city,
							postalCode: hubReference.address.postalCode,
							street: hubReference.address.street,
							number: hubReference.address.number ? Number(hubReference.address.number) : undefined,
							numberAddition: hubReference.address.numberAddition,
						}
					:	undefined,
				organisation: BookingsService.fromBaseReferenceResponse(hubReference.organisationReference),
				...BookingsService.fromBaseReferenceResponse(hubReference),
			},
		};
	}

	static fromSlimResponse(data: BookingSlimResponse): Booking {
		const { id, name, start, end, hub, instance, user, ...rest } = data;

		return {
			...rest,
			...BookingsService.fromBaseReferenceResponse({
				id: id,
				name: name,
			}),
			period: {
				start: new Date(start),
				end: new Date(end),
			},
			user: BookingsService.fromBaseReferenceResponse(user),
			item: BookingsService.fromBaseReferenceResponse(instance),
			hub: BookingsService.fromBaseReferenceResponse(hub),
		};
	}

	static toRequest(data: BookingWriteable): BookingRequest {
		const { ...rest } = data;

		return {
			...rest,
		};
	}
}

interface BaseBookingResponse extends BaseReferenceResponse {
	status: BookingStatusEnum;
	approvalState: ApprovalStateEnum;
	usageState: UsageStateEnum;
	attentionStates: string[];
	billingType: 'personal' | 'business';
	price: 'todo';
}

interface BookingSlimResponse extends BaseBookingResponse {
	start: string;
	end: string;
	instance: BaseReferenceResponse;
	user: BaseReferenceResponse;
	hub: BaseReferenceResponse;
}

interface BookingResponse extends BaseBookingResponse {
	startAddress?: string;
	stopAddress?: string;
	periodStart: string;
	periodEnd: string;
	created: string;
	userReference: BaseReferenceResponse;
	hubReference: {
		latitude?: number;
		longitude?: number;
		address: AddressResponse;
		organisationReference: BaseReferenceResponse;
	} & BaseReferenceResponse;
	itemReference: BaseReferenceResponse;
	itemInstance: {
		deviceId?: string;
	} & BaseReferenceResponse;
	paymentStatus: string;
	priceDetails: 'todo';
	createdByUser: BaseReferenceResponse;
	comment: string;
}

interface BookingRequest {}

export default BookingsService;
